import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  Database, 
  Trash2, 
  Users, 
  MessageSquare, 
  CreditCard, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  BarChart3,
  Shield,
  Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';

const DatabaseManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    if (user?.is_admin) {
      fetchDatabaseStats();
    } else {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchDatabaseStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/database-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching database stats:', error);
      toast.error('Veritabanı istatistikleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Süresi dolmuş verileri temizlemek istediğinizden emin misiniz?')) {
      return;
    }

    setCleaning(true);
    try {
      await axios.post('/api/admin/cleanup');
      toast.success('Veri temizliği tamamlandı');
      fetchDatabaseStats(); // İstatistikleri yenile
    } catch (error) {
      console.error('Error cleaning data:', error);
      toast.error('Veri temizliği başarısız');
    } finally {
      setCleaning(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('tr-TR').format(num);
  };

  const getUserTypeLabel = (type) => {
    const labels = {
      'guest': 'Misafir',
      'google': 'Google',
      'admin': 'Admin'
    };
    return labels[type] || type;
  };

  const getUserTypeColor = (type) => {
    const colors = {
      'guest': 'text-yellow-400 bg-yellow-400/10',
      'google': 'text-blue-400 bg-blue-400/10',
      'admin': 'text-red-400 bg-red-400/10'
    };
    return colors[type] || 'text-gray-400 bg-gray-400/10';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Database className="w-10 h-10 text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Veritabanı Yönetimi</h1>
              <p className="text-gray-400">Kullanıcı verilerini ve veri saklama politikalarını yönetin</p>
            </div>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={fetchDatabaseStats}
              className="flex items-center space-x-2 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Yenile</span>
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Shield className="w-4 h-4" />
              <span>Admin Panel</span>
            </button>
          </div>
        </div>

        {stats && (
          <div className="space-y-8">
            {/* Kullanıcı Dağılımı */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <Users className="w-6 h-6" />
                <span>Kullanıcı Dağılımı</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.user_distribution.map((item, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getUserTypeColor(item.type)}`}>
                        {getUserTypeLabel(item.type)}
                      </span>
                      <span className="text-2xl font-bold text-white">{formatNumber(item.count)}</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(item.count / stats.user_distribution.reduce((sum, u) => sum + u.count, 0) * 100) || 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Veri Saklama Durumu */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <Clock className="w-6 h-6" />
                <span>Veri Saklama Durumu</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <Users className="w-8 h-8 text-yellow-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Misafir Kullanıcılar</h3>
                      <p className="text-sm text-gray-400">7 gün sonra silinir</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-yellow-400">{formatNumber(stats.data_retention.guest_users)}</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <Shield className="w-8 h-8 text-blue-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Google Kullanıcılar</h3>
                      <p className="text-sm text-gray-400">Kalıcı saklanır</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-blue-400">{formatNumber(stats.data_retention.google_users)}</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <Database className="w-8 h-8 text-red-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Admin Kullanıcılar</h3>
                      <p className="text-sm text-gray-400">Kalıcı saklanır</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-red-400">{formatNumber(stats.data_retention.admin_users)}</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <AlertTriangle className="w-8 h-8 text-orange-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Süresi Dolacak</h3>
                      <p className="text-sm text-gray-400">24 saat içinde</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-orange-400">{formatNumber(stats.data_retention.expiring_guests)}</p>
                </div>
              </div>
            </motion.div>

            {/* Toplam Veri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <BarChart3 className="w-6 h-6" />
                <span>Toplam Veri</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <MessageSquare className="w-8 h-8 text-green-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Toplam Sohbet</h3>
                      <p className="text-sm text-gray-400">Tüm kullanıcılar</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-green-400">{formatNumber(stats.total_data.chats)}</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <MessageSquare className="w-8 h-8 text-purple-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Toplam Mesaj</h3>
                      <p className="text-sm text-gray-400">Tüm sohbetler</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-purple-400">{formatNumber(stats.total_data.messages)}</p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <CreditCard className="w-8 h-8 text-yellow-400" />
                    <div>
                      <h3 className="text-lg font-semibold text-white">Toplam Ödeme</h3>
                      <p className="text-sm text-gray-400">Kayıt sayısı</p>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-yellow-400">{formatNumber(stats.total_data.payments)}</p>
                </div>
              </div>
            </motion.div>

            {/* Veri Temizleme */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <Trash2 className="w-6 h-6" />
                <span>Veri Temizleme</span>
              </h2>
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 mt-0.5" />
                  <div>
                    <h3 className="text-yellow-400 font-semibold mb-2">Otomatik Temizlik</h3>
                    <p className="text-gray-300 text-sm mb-2">
                      Sistem otomatik olarak süresi dolmuş misafir kullanıcı verilerini temizler:
                    </p>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Her saat başı kontrol yapılır</li>
                      <li>• 7 günden eski misafir kullanıcı verileri silinir</li>
                      <li>• 30 günden eski mesajlar temizlenir</li>
                      <li>• Google ve admin kullanıcı verileri korunur</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={handleCleanup}
                  disabled={cleaning}
                  className="flex items-center space-x-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {cleaning ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                  <span>{cleaning ? 'Temizleniyor...' : 'Manuel Temizlik'}</span>
                </button>
                
                <div className="flex items-center space-x-2 text-gray-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm">Son temizlik: Otomatik</span>
                </div>
              </div>
            </motion.div>

            {/* Veri Saklama Politikası */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gray-800 rounded-xl p-6"
            >
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <Calendar className="w-6 h-6" />
                <span>Veri Saklama Politikası</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <h3 className="text-yellow-400 font-semibold mb-2">Misafir Kullanıcılar</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• 7 gün veri saklama</li>
                    <li>• Sohbetler otomatik silinir</li>
                    <li>• Mesajlar otomatik silinir</li>
                    <li>• Ödeme kayıtları silinir</li>
                  </ul>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <h3 className="text-blue-400 font-semibold mb-2">Google Kullanıcılar</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Kalıcı veri saklama</li>
                    <li>• Sohbetler korunur</li>
                    <li>• Mesajlar korunur</li>
                    <li>• Ödeme kayıtları korunur</li>
                  </ul>
                </div>
                
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <h3 className="text-red-400 font-semibold mb-2">Admin Kullanıcılar</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Kalıcı veri saklama</li>
                    <li>• Tüm veriler korunur</li>
                    <li>• Yönetim erişimi</li>
                    <li>• Özel yetkiler</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseManagement;
